<!--
  CollectorReportSheet — «بلّغ من الرف» (collector-app P1-D, handoff spec
  Section 2.7; mockup Report.dc.html).

  What the collector found on the shelf instead: photos, the colour chips
  (the catalogue's own colour words when the API sends them, plus three fixed
  chips), free text, how many he found and the shelf price. «ابعت للّيدر» is
  ONE `facts` tap {foundQty, priceCents, photoIds, options, note} — the sheet
  emits it, the shop page hands it to the action layer. It never marks the
  line: the item stays unmarked until she answers or he marks it.

  CHIPS (collector-app P3-C, L-B): a colour word travels as `options[]` (at
  most 6 — the backend refuses a longer list for the whole batch); the three
  fixed chips are statements and join the note text. The latest `options`
  list REPLACES the leader's, so the sheet opens with the colours he already
  reported selected: a second report that only added a photo must not take
  the first one's colours away. What he types here is the pre-filled question
  the leader reviews and sends to her (L-D).

  PHOTOS never block anything (collector-app P2-C): they are taken here and
  shrunk on the phone (collector/photos.js); each tile shows the picture at
  once. Nothing is kept until «ابعت للّيدر»: THEN each photo is written to the
  phone with its uuid (collector/photoQueue.js) and the `facts` tap names it by
  the id the server will give it — so the tap goes into the queue like any
  other, with no signal too, and does not wait for a single upload. The
  pictures follow in the background once every tap is sent. The mark buttons
  are outside this sheet and never wait for any of it.

  Fresh state on every open. Definite height + the action in the fixed footer
  (DepositSheet pattern, memory f7-sheet-auto-height-kills-scroll).
-->
<template>
  <f7-sheet
    class="collector-surface c-report-sheet"
    :opened="opened"
    backdrop
    close-by-backdrop-click
    @sheet:closed="onClosed"
  >
    <f7-page-content v-if="line">
      <div class="c-report">
        <div class="c-report__head">
          <div class="c-report__heading">
            <h2 class="c-report__title">{{ t('collector.report.title') }}</h2>
            <div class="c-report__sub c-num">
              {{ t('collector.report.sub', { product: line.productName, code: line.memberCode ?? '—' }) }}
            </div>
          </div>
          <button
            type="button"
            class="c-reset c-report__close"
            :aria-label="t('collector.detail.close')"
            @click="emit('update:opened', false)"
          >
            <svg width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.4"
              stroke-linecap="round"
              aria-hidden="true"
            ><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>

        <!-- ── photos ── -->
        <section class="c-block">
          <div class="c-block__title">{{ t('collector.report.photoTitle') }}</div>
          <div class="c-shots">
            <label v-if="room > tiles.length" class="c-shoot">
              <input
                class="c-shoot__input"
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                @change="onFiles"
              />
              <svg width="26"
                height="26"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              ><path d="M4 8h3l2-3h6l2 3h3v11H4z" /><circle cx="12" cy="13" r="3.5" /></svg>
              <span>{{ t('collector.report.shoot') }}</span>
            </label>
            <button
              v-for="tile in tiles"
              :key="tile.uuid"
              type="button"
              class="c-reset c-shot"
              :class="`c-shot--${tile.status}`"
              :aria-label="tile.status === 'failed' ? t('collector.report.photoFailed') : t('collector.report.photoRemove')"
              @click="onTile(tile)"
            >
              <img :src="tile.preview" alt="" draggable="false" />
              <span v-if="tile.status === 'failed'" class="c-shot__state c-shot__state--failed">!</span>
              <span v-else-if="tile.status === 'preparing'" class="c-shot__state">…</span>
            </button>
          </div>
          <div v-if="failedCount > 0" class="c-report__warn">{{ t('collector.report.photoFailed') }}</div>
          <div v-else class="c-report__note">{{ t('collector.report.photoNote') }}</div>
        </section>

        <!-- ── what is available instead ── -->
        <section class="c-block">
          <label class="c-block__title" for="c-report-text">{{ t('collector.report.availableTitle') }}</label>
          <div class="c-chips">
            <button
              v-for="chip in chipList"
              :key="chip"
              type="button"
              class="c-reset c-pick"
              :class="{ 'c-pick--on': chips.includes(chip) }"
              :aria-pressed="chips.includes(chip)"
              :disabled="isFull(chip)"
              @click="toggleChip(chip)"
            >
              {{ chips.includes(chip) ? `${chip} ✓` : chip }}
            </button>
          </div>
          <input
            id="c-report-text"
            v-model="text"
            class="c-field"
            type="text"
            :placeholder="t('collector.report.textPlaceholder')"
            @focus="onFocus"
          />
          <div class="c-report__note">{{ t('collector.report.hint') }}</div>
        </section>

        <!-- ── how many, at what price ── -->
        <section class="c-pair">
          <div class="c-block c-block--half">
            <label class="c-block__title" for="c-report-qty">{{ t('collector.report.foundQty') }}</label>
            <div class="c-stepper">
              <button
                type="button"
                class="c-reset c-step"
                :aria-label="t('collector.report.less')"
                @click="stepQty(-1)"
              >
                −
              </button>
              <input
                id="c-report-qty"
                v-model="qtyText"
                class="c-field c-field--qty c-num"
                type="text"
                inputmode="numeric"
                :class="{ 'c-field--bad': qty == null }"
                @focus="onFocus"
              />
              <button
                type="button"
                class="c-reset c-step"
                :aria-label="t('collector.report.more')"
                @click="stepQty(1)"
              >
                +
              </button>
            </div>
            <div class="c-report__note c-num">{{ t('collector.report.required', { qty: line.qty }) }}</div>
          </div>

          <div class="c-block c-block--half">
            <label class="c-block__title" for="c-report-price">{{ t('collector.report.price') }}</label>
            <div class="c-money">
              <input
                id="c-report-price"
                v-model="priceText"
                class="c-field c-field--money c-num"
                type="text"
                inputmode="decimal"
                :class="{ 'c-field--bad': priceBad }"
                @focus="onFocus"
              />
              <span class="c-money__unit">{{ t('collector.cash.unit') }}</span>
            </div>
            <div class="c-report__note c-num">
              {{ t('collector.report.priceHint', { price: pounds(line.unitPriceCents) }) }}
            </div>
          </div>
        </section>

        <div class="c-report__footer">{{ t('collector.report.footer') }}</div>
      </div>
    </f7-page-content>

    <template #fixed>
      <div v-if="line" class="sheet-fixed-footer">
        <button
          type="button"
          class="c-reset c-send"
          :disabled="!canSend"
          @click="send"
        >
          <span>{{ sendLabel }}</span>
          <svg width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.4"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
            class="c-flip-rtl"
          ><path d="M9 6l6 6-6 6" /></svg>
        </button>
      </div>
    </template>
  </f7-sheet>
</template>

<script setup>
import { computed, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { newActionId } from '@/collector/ids.js';
import {
  FIXED_REPORT_CHIPS,
  REPORT_OPTIONS_MAX,
  centsToInputText,
  factsPayload,
  parsePoundsToCents,
  parseWholeNumber,
  reportHasContent,
  reportOptionLabels,
  shelfPhotoRoom,
} from '@/collector/lineView.js';
import { useCollectorPhotosStore } from '@/collector/photoQueue.js';
import { prepareCollectorPhoto } from '@/collector/photos.js';
import { useCurrency } from '@/composables/useCurrency.js';

const props = defineProps({
  opened: { type: Boolean, default: false },
  /** The line being reported on (from the day document). */
  line: { type: Object, default: null },
});
/** `send` carries `(line, payload)` — the `facts` payload the page queues. */
const emit = defineEmits(['update:opened', 'send']);

const { t } = useI18n();
const photos = useCollectorPhotosStore();
const { formatAmount } = useCurrency();
const pounds = (cents) => formatAmount(cents, { trimZeros: true });

// ── The draft ─────────────────────────────────────────────────────────────────

const chips = ref([]);
const text = ref('');
const qtyText = ref('');
const priceText = ref('');
/** Photo tiles: `{ uuid, preview, blob, status: 'preparing'|'ready'|'failed' }` — `uuid` only tells tiles apart here. */
const tiles = ref([]);

/** The colours the leader already holds from an earlier report on this line. */
const heldOptions = computed(() => reportOptionLabels(props.line?.collectorFacts?.options));

/** A price is at least one piastre: the backend refuses `priceCents` below 1, and that costs the whole report. */
const priceOrNull = (cents) => (Number(cents) > 0 ? Number(cents) : null);

const prefill = computed(() => ({
  foundQty: Number(props.line?.qty) || 0,
  // The day read sends a line with no price as 0 — that is "no price", so the field opens empty.
  priceCents: priceOrNull(props.line?.unitPriceCents),
  options: heldOptions.value,
}));

function reset() {
  releaseTiles();
  chips.value = [...heldOptions.value];
  text.value = '';
  qtyText.value = String(prefill.value.foundQty);
  priceText.value = centsToInputText(prefill.value.priceCents);
  tiles.value = [];
}

// Two sources, compared one by one: the draft starts over only when the sheet
// opens or moves to another line. A getter returning `[opened, id]` builds a
// new array each run, so it fired on EVERY rebuild of the day — and with taps
// waiting offline the day is rebuilt on each retry (and on the return from the
// camera), which wiped a photo he had just taken.
watch(
  [() => props.opened, () => props.line?.id],
  ([opened]) => {
    if (opened) reset();
  },
  { immediate: true },
);

const qty = computed(() => parseWholeNumber(qtyText.value));
/** A typed 0 is not a price either: it reads as a bad amount and holds the send. */
const priceCents = computed(() => (priceText.value.trim() === '' ? null : priceOrNull(parsePoundsToCents(priceText.value))));
const priceBad = computed(() => priceText.value.trim() !== '' && priceCents.value == null);

function stepQty(delta) {
  const base = qty.value ?? prefill.value.foundQty;
  qtyText.value = String(Math.max(0, base + delta));
}

/** The three fixed chips' words — statements, not colours (they join the note). */
const fixedChips = computed(() => FIXED_REPORT_CHIPS.map((key) => t(`collector.report.chip.${key}`)));

/** The catalogue's colour words, the ones already reported (so they can be taken off), then the three fixed chips. */
const chipList = computed(() => {
  const colours = (Array.isArray(props.line?.availableColours) ? props.line.availableColours : [])
    .map((word) => String(word).trim())
    .filter(Boolean);
  return [...new Set([...colours, ...heldOptions.value, ...fixedChips.value])];
});

const pickedColours = computed(() => chips.value.filter((chip) => !fixedChips.value.includes(chip)));

/** A colour past the backend's cap cannot be added: it would refuse the whole batch. */
function isFull(chip) {
  return (
    !chips.value.includes(chip) &&
    !fixedChips.value.includes(chip) &&
    pickedColours.value.length >= REPORT_OPTIONS_MAX
  );
}

function toggleChip(chip) {
  if (isFull(chip)) return;
  chips.value = chips.value.includes(chip) ? chips.value.filter((item) => item !== chip) : [...chips.value, chip];
}

// ── Photos: shrink, show, keep on «ابعت» — nothing waits for the network ─────

const room = computed(() => shelfPhotoRoom(props.line));
const failedCount = computed(() => tiles.value.filter((tile) => tile.status === 'failed').length);
const inFlight = computed(() => tiles.value.some((tile) => tile.status === 'preparing'));
const readyTiles = computed(() => tiles.value.filter((tile) => tile.status === 'ready'));

function patchTile(uuid, patch) {
  tiles.value = tiles.value.map((tile) => (tile.uuid === uuid ? { ...tile, ...patch } : tile));
}

async function onFiles(event) {
  const input = event.target;
  const files = Array.from(input.files ?? []).slice(0, Math.max(0, room.value - tiles.value.length));
  input.value = '';
  for (const file of files) {
    const uuid = newActionId();
    const tile = { uuid, preview: URL.createObjectURL(file), blob: null, status: 'preparing' };
    tiles.value = [...tiles.value, tile];
    try {
      patchTile(uuid, { blob: await prepareCollectorPhoto(file), status: 'ready' });
    } catch {
      patchTile(uuid, { status: 'failed' });
    }
  }
}

/** A tile that is ready is taken off the report; one that failed to shrink goes too, to shoot again. */
function onTile(tile) {
  if (tile.status === 'ready' || tile.status === 'failed') {
    URL.revokeObjectURL(tile.preview);
    tiles.value = tiles.value.filter((item) => item.uuid !== tile.uuid);
  }
}

function releaseTiles() {
  for (const tile of tiles.value) URL.revokeObjectURL(tile.preview);
}

// ── Sending ───────────────────────────────────────────────────────────────────

const sending = ref(false);

/** `photoIds` here only tells `reportHasContent` there are photos; the real ids are made on send. */
const draft = computed(() => ({
  foundQty: qty.value,
  priceCents: priceCents.value,
  photoIds: readyTiles.value.map((tile) => tile.uuid),
  chips: chips.value,
  fixedChips: fixedChips.value,
  text: text.value,
}));

const canSend = computed(
  () => !inFlight.value && !sending.value && qty.value != null && !priceBad.value && reportHasContent(draft.value, prefill.value),
);

const sendLabel = computed(() => (inFlight.value ? t('collector.report.waitingPhotos') : t('collector.report.send')));

/**
 * Write each photo to the phone first (write first, like every tap), then hand
 * the page ONE `facts` payload naming them by the ids the server will give.
 * A photo the phone cannot keep is left out (logged) — it never costs the report.
 */
async function send() {
  if (!canSend.value || !props.line) return;
  sending.value = true;
  try {
    const photoIds = [];
    for (const tile of readyTiles.value) {
      try {
        photoIds.push((await photos.add({ blob: tile.blob, lineId: props.line.id })).id);
      } catch (error) {
        console.error('[CollectorReportSheet] a photo could not be kept on the phone', error);
      }
    }
    emit('send', props.line, factsPayload({ ...draft.value, photoIds, heldOptions: heldOptions.value }));
  } finally {
    sending.value = false;
  }
}

/** A field under the keyboard scrolls into view once the keyboard is up. */
function onFocus(event) {
  const el = event.target;
  setTimeout(() => el?.scrollIntoView?.({ block: 'center', behavior: 'smooth' }), 300);
}

function onClosed() {
  releaseTiles();
  if (props.opened) emit('update:opened', false);
}

onUnmounted(releaseTiles);
</script>

<style scoped>
.c-report-sheet {
  height: 92%;
  background: var(--c-ground);
  --sheet-fixed-footer-h: 80px;
  --f7-page-content-extra-padding-bottom: var(--sheet-fixed-footer-h);
}

.c-report {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px 16px 8px;
}

.c-report__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.c-report__title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
}

.c-report__sub {
  margin-top: 2px;
  font-size: 13px;
  color: var(--c-muted);
}

.c-report__close {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: var(--c-radius-chip);
  background: var(--c-card);
  color: var(--c-ink);
}

.c-block {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  border-radius: var(--c-radius-row);
  background: var(--c-card);
}

.c-block__title {
  font-size: 15px;
  font-weight: 700;
}

.c-report__note {
  font-size: 12px;
  color: var(--c-muted);
  line-height: 1.5;
}

.c-report__warn {
  font-size: 12px;
  font-weight: 700;
  color: var(--c-red-text);
}

.c-report__footer {
  padding: 0 8px;
  font-size: 12px;
  color: var(--c-muted);
  text-align: center;
}

.c-shots {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.c-shoot {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  width: 84px;
  height: 84px;
  border: 2px dashed var(--c-purple);
  border-radius: var(--c-radius-chip);
  color: var(--c-indigo);
  font-size: 13px;
  font-weight: 700;
  box-sizing: border-box;
  cursor: pointer;
}

.c-shoot__input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

.c-shot {
  position: relative;
  width: 84px;
  height: 84px;
  overflow: hidden;
  border-radius: var(--c-radius-chip);
  background: var(--c-lilac);
}

.c-shot img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.c-shot--preparing img { opacity: 0.55; }

.c-shot__state {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--c-indigo);
  font-size: 24px;
  font-weight: 700;
}

.c-shot__state--failed {
  background: rgba(254, 226, 226, 0.85);
  color: var(--c-red-text);
}

.c-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.c-pick {
  min-height: 44px;
  padding: 0 14px;
  border: 1px solid var(--c-border);
  border-radius: var(--c-radius-chip);
  background: var(--c-ground);
  color: var(--c-ink);
  font-size: 14px;
  font-weight: 700;
}

.c-pick--on {
  border: 2px solid var(--c-indigo);
  background: var(--c-lilac);
  color: var(--c-indigo);
}

.c-pick:disabled { opacity: 0.45; }

.c-field {
  width: 100%;
  height: 48px;
  padding: 0 14px;
  border: 1px solid var(--c-border);
  border-radius: var(--c-radius-chip);
  background: var(--c-ground);
  color: var(--c-ink);
  font: inherit;
  font-size: 16px;
  box-sizing: border-box;
}

.c-field--bad { border-color: var(--c-red-border); }

.c-pair {
  display: flex;
  gap: 10px;
}

.c-block--half {
  flex: 1 1 0;
  min-width: 0;
}

.c-stepper {
  display: flex;
  align-items: center;
  gap: 6px;
}

.c-step {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border: 1px solid var(--c-border);
  border-radius: var(--c-radius-chip);
  background: var(--c-ground);
  color: var(--c-indigo);
  font-size: 22px;
  font-weight: 700;
}

.c-field--qty {
  min-width: 0;
  padding: 0;
  text-align: center;
  font-weight: 700;
}

.c-money {
  display: flex;
  align-items: center;
  gap: 8px;
}

.c-field--money { text-align: center; font-weight: 700; }

.c-money__unit {
  flex-shrink: 0;
  font-size: 13px;
  color: var(--c-muted);
}

.c-send {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  height: 56px;
  border-radius: var(--c-radius-row);
  background: var(--c-indigo);
  color: #ffffff;
  font-size: 18px;
  font-weight: 700;
}

.c-send:disabled {
  background: var(--c-dot);
  color: var(--c-ink-soft);
}
</style>

<!-- non-scoped: the send arrow points the way forward (›), which reads leftward
     in RTL — it flips with the reading direction on <html> (CLAUDE.md: never
     :global(ancestor) .child). -->
<style>
[dir='rtl'] .c-report-sheet .c-flip-rtl {
  transform: scaleX(-1);
}

[dir='ltr'] .c-report-sheet .c-flip-rtl {
  transform: scaleX(1);
}
</style>
